/*
 * This kind of conversion could be done in the API. Putting it here for the time being.
 */
import { HgListingCardImage, hgNumber, hgPrice, HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  Address,
  Attachment,
  AttachmentType,
  Buy,
  L10N,
  Listing,
  OfferType,
  Prices,
  Rent,
  Url,
  UrlType,
} from '@smg-real-estate/hg-rets-schema';

import {
  getCloudinaryUrlForTransformation,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import { truncateText } from '@/helpers/TextFormatting/TextFormatting';
import { type HgListingAttachedDocument } from '@/models/HgListingAttachedDocument';
import {
  getEmbeddedYouTubeUrl,
  HgListingVideoMovieTypes,
  isYouTubeVideoUrl,
  type HgListingVideoMovie,
} from '@/models/HgListingVideoMovie';
import {
  getVirtualTourUrl,
  HgListingVirtualTourTypes,
  isVirtualTourUrl,
  type HgListingVirtualTour,
} from '@/models/HgListingVirtualTour';
import type { ListingItem } from '@smg-real-estate/hg-search-libs';
import type { Composer } from 'vue-i18n';

const REPRESENTATIVE_BLURRED_EXCLUSIVE_IMAGE_URL =
  'https://media.homegate.ch/assets/representative_blurred_exclusive_image.png';
export function getListingDefaultLocale(listing: Listing): L10N | undefined {
  return (
    listing.localization?.[listing.localization?.primary as HgSupportedLanguage] ??
    listing.localization?.de
  );
}

export function getListingImages(listing: Listing): Attachment[] {
  return (
    getListingDefaultLocale(listing)?.attachments?.filter(
      (attachment) => attachment.type === AttachmentType.Image,
    ) || []
  );
}

export function getListingImage(listing: Listing, index: number): Attachment | undefined {
  return getListingImages(listing)[index];
}

export function getListingPriceData(listing: Listing): Buy | Rent | undefined {
  return listing.offerType === OfferType.Buy ? listing.prices?.buy : listing.prices?.rent;
}

export function getListingCurrency(listing: Listing): string | undefined {
  return listing.prices?.currency;
}

export function getListingPriceArea(listing: Listing): string | undefined {
  return getListingPriceData(listing)?.area;
}

export function getListingPriceInterval(listing: Listing): string | undefined {
  return listing.offerType === OfferType.Rent ? listing.prices?.rent?.interval : undefined;
}

export function getListingDisplayPrice(listing: Listing): number | undefined {
  const priceData = getListingPriceData(listing);

  if (!priceData) {
    return undefined;
  }

  return (priceData as Rent).gross ?? (priceData as Rent).net ?? (priceData as Buy).price;
}

export function getListingRooms(listing?: Listing): number | undefined {
  return listing?.characteristics?.numberOfRooms;
}

export function getListingLivingSpace(listing?: Listing): number | undefined {
  return listing?.characteristics?.livingSpace;
}

export function getListingLink(listingDefaultLocale: L10N): Url | null {
  return (
    listingDefaultLocale.urls?.find((url: Url) => {
      const isLink = url.type && url.type === UrlType.Link;
      return isLink && url.value && !isVirtualTourUrl(url.value);
    }) ?? null
  );
}

const getListingUrls = (listingDefaultLocale: L10N | undefined): Url[] =>
  listingDefaultLocale?.urls ?? [];

const getListingAttachments = (listingDefaultLocale: L10N | undefined): Attachment[] =>
  listingDefaultLocale?.attachments ?? [];

export function getListingVirtualTour(
  listingDefaultLocale: L10N | undefined,
): HgListingVirtualTour | undefined {
  const listingVirtualTours: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => url.type && url.type in HgListingVirtualTourTypes)
    .map((url) => ({
      src: url.value,
    }));

  if (listingVirtualTours.length > 0) {
    return listingVirtualTours[0];
  }

  // Additionally search in the urls for type LINK (legacy).
  const listingLinks: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isLink = url.type && url.type === UrlType.Link;
      return isLink && url.value && isVirtualTourUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getVirtualTourUrl(url.value),
    }));

  return listingLinks.length > 0 ? listingLinks[0] : undefined;
}

export function getListingVideoMovie(
  listingDefaultLocale: L10N,
  locale = 'de',
): HgListingVideoMovie | undefined {
  const listingVideos: HgListingVideoMovie[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isMovie = url.type && url.type in HgListingVideoMovieTypes;
      return isMovie && url.value && isYouTubeVideoUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getEmbeddedYouTubeUrl(url.value, locale),
      title: url.title,
    }));

  if (listingVideos.length > 0) {
    return listingVideos[0];
  }

  // Additionally search in the attachment for videos that are a YouTube video.
  const listingMovies: HgListingVideoMovie[] = getListingAttachments(listingDefaultLocale)
    .filter((attachment) => {
      const isMovie = attachment.type && attachment.type === AttachmentType.Movie;
      return isMovie && attachment.url && isYouTubeVideoUrl(attachment.url);
    })
    .map((attachment) => ({
      src: attachment.url && getEmbeddedYouTubeUrl(attachment.url, locale),
      title: attachment.title,
    }));

  return listingMovies.length > 0 ? listingMovies[0] : undefined;
}

export function getListingAttachedDocument(
  listingDefaultLocale: L10N,
): HgListingAttachedDocument[] {
  const listingDocuments: HgListingAttachedDocument[] = getListingAttachments(listingDefaultLocale)
    .filter((attachment: Attachment) => {
      return (
        attachment.type &&
        [AttachmentType.Document, AttachmentType.Plan, AttachmentType.SalesBrochure].includes(
          attachment.type,
        )
      );
    })
    .map((attachment: Attachment) => ({
      href: attachment.url,
      label: attachment.title,
    }));

  return listingDocuments;
}

export function getFirstListingAttachedDocument(
  listingDefaultLocale: L10N,
): HgListingAttachedDocument | undefined {
  const listingDocuments = getListingAttachedDocument(listingDefaultLocale);
  return listingDocuments.length > 0 ? listingDocuments[0] : undefined;
}

export const listingIdsFrom = (listings: ListingItem[]): string[] =>
  listings.map((listing) => listing.id || '');

export const getRentGross = (prices: Prices, locale: string): string | undefined =>
  prices?.rent?.gross ? hgPrice(prices.rent.gross, locale as HgSupportedLanguage) : undefined;

export const getBuyPrice = (prices: Prices, locale: string): string | undefined =>
  prices?.buy?.price ? hgPrice(prices.buy.price, locale as HgSupportedLanguage) : undefined;

export function getStreetWithAddition(address: Address): string | undefined {
  return address.streetAddition && address.street
    ? address.street + address.streetAddition
    : address.street;
}

export function getStreet(listing: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  return getStreetWithAddition(address);
}

export function getPostalCodeWithLocality(listing: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  return [address.postalCode, address.locality].filter((item) => !!item).join(', ');
}

export function getListingAddress(listing?: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  const streetAndZip = [getStreetWithAddition(address), address.postalCode]
    .filter((item) => !!item)
    .join(', ');

  return [streetAndZip, address.locality].filter((item) => !!item).join(' ');
}

export function getListingFormattedPrice(listing: Listing, locale: string): string | null {
  const price = getListingDisplayPrice(listing);

  if (price === undefined || !listing.prices || price === 0) {
    return null;
  }
  return [listing.prices.currency, hgPrice(price, locale as HgSupportedLanguage)]
    .filter((item) => !!item)
    .join(' ');
}

export function getAvailability(listing: Listing, i18n: Composer): string {
  if (!listing.availableFrom) {
    return i18n.t('availability.BY_AGREEMENT').toString();
  }

  const availabilityDate = new Date(listing.availableFrom);
  if (availabilityDate.getTime() <= Date.now()) {
    return i18n.t('availability.IMMEDIATELY').toString();
  }

  return availabilityDate.toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function getNewListingState(listing: Listing): boolean {
  if (listing?.meta?.createdAt) {
    const today = new Date();
    const createdAt = new Date(listing?.meta?.createdAt);
    // Block if the listing is created in the future
    if (today.getTime() < createdAt.getTime()) {
      return false;
    }
    // Visible for one day (24 hours) from the creation date
    const maxDisplayDate = new Date(listing?.meta?.createdAt);
    maxDisplayDate.setDate(maxDisplayDate.getDate() + 1);

    return maxDisplayDate.getTime() >= today.getTime();
  }

  return false;
}

const wincasaListerIds = [
  'wic',
  'wicflf',
  '198822',
  'csbl',
  'isx1000225',
  'isx1089572',
  'isx1120201',
  'isx1158768',
  'isx192572',
  'isx312933',
  'isx46578',
  'jel',
  'wick',
  'wickar',
  'wicneulok',
  'wicNLgug',
  'wicp',
  'wict360',
  'wicvas',
  '198884',
  '198877',
  '198886',
  '198888',
  '198887',
  '198892',
  '198891',
  '198882',
  '198883',
  '198889',
  'jelge',
  '198885',
];

export const isWincasaListing = (listerId: string): boolean => {
  return wincasaListerIds.includes(listerId);
};

export function isNonSwissBorderRegion(postalCode: string | undefined): boolean {
  return postalCode === '8238';
}

export const getListingPrice = (listing: Listing, i18n: Composer): string => {
  return listing
    ? `${getListingCurrency(listing)} ${getListingDisplayPrice(listing)}`
    : i18n.t('ON_REQUEST').toString();
};

export const getListingFormattedRooms = (listing: Listing, i18n: Composer): string => {
  const rooms = getListingRooms(listing);

  if (typeof rooms === 'undefined') {
    return '';
  }

  return `${rooms} ${i18n.t('ROOMS')}`;
};

export const getListingFormattedLivingSpace = (listing: Listing): string => {
  const livingSpace = getListingLivingSpace(listing);
  if (!livingSpace) {
    return '';
  }

  return hgNumber(livingSpace) + ' m²';
};

export const getListingTitle = (listing: Listing, i18n: Composer): string => {
  const listingDefaultLocale = getListingDefaultLocale(listing);
  const listingTitle = listingDefaultLocale?.text?.title;
  const rooms = getListingFormattedRooms(listing, i18n);

  if (!listing?.address?.street || !getListingDisplayPrice(listing) || !rooms) {
    return i18n
      .t('metaInfo.pdp.titleFallbackTemplate', {
        title: listingTitle,
      })
      .toString();
  }

  return i18n
    .t('metaInfo.pdp.titleTemplate', {
      listingTitle: listingTitle ? truncateText(listingTitle, 40) : '',
      location: listing.address && getStreetWithAddition(listing.address),
      price: getListingPrice(listing, i18n),
      rooms: rooms,
    })
    .toString();
};

export function getDisplayingListingImages(listing: Listing): HgListingCardImage[] {
  const images = getListingImages(listing);
  const numberOfImages = listing.valueAddedServices?.numberOfImages;
  const listingCardImages = images.map((attachment) => ({
    file: attachment.file || '',
    type: attachment.type?.toString() || '',
    url: attachment.url || '',
    alt: attachment.alt || '',
  }));

  if (numberOfImages && numberOfImages > images.length) {
    const numberOfImagesToAdd = numberOfImages - images.length;

    for (let i = 0; i < numberOfImagesToAdd; i++) {
      listingCardImages.push({
        file: '',
        type: AttachmentType.Image,
        url: REPRESENTATIVE_BLURRED_EXCLUSIVE_IMAGE_URL,
        alt: '',
      });
    }
  }

  return listingCardImages;
}

/**
 * Returns the index where exclusive images start.
 * Returns -1 if there are no exclusive images or if the listing is invalid.
 */
// export const getExclusiveImageStartIndex = (listing: Listing | undefined): number => {
//   if (!listing) {
//     return -1;
//   }

//   const availableImages = getListingImages(listing);
//   const numberOfImages = listing.valueAddedServices?.numberOfImages;
//   if (
//     availableImages.length === 0 ||
//     numberOfImages === undefined ||
//     numberOfImages >= availableImages.length
//   ) {
//     return -1;
//   }
//   return numberOfImages;
// };

export const getListingImageAttachments = (
  listing?: Listing,
):
  | {
      src: string;
      alt: string;
    }[]
  | undefined => {
  if (!listing) {
    return undefined;
  }

  const listingImageAttachments = getListingImages(listing);

  const listingTitle = getListingDefaultLocale(listing)?.text?.title ?? '';

  return listingImageAttachments
    .filter((attachment: Attachment) => !!attachment.url)
    .map((attachment: Attachment, index) => {
      return {
        alt: attachment.caption || attachment.title || `${listingTitle} (${index + 1})`,
        src: attachment.url as string,
      };
    });
};

export const getTransformedListingImageUrl = (listing: Listing): string | undefined => {
  const listingImages = getListingImageAttachments(listing);
  if (!listingImages || listingImages.length === 0) {
    return undefined;
  }

  return getCloudinaryUrlForTransformation(
    listingImages[0].src,
    HgCloudinaryNamedTransformation.SMALL,
  );
};
